<template>
  <PageLayout :title="$t('admin.users_title')" :subtitle="$t('admin.users_subtitle')">
    <ContentCard>
      <div class="d-flex flex-wrap mb-3 gap-2">
        <input v-model.trim="search" type="search" class="form-control form-control-sm" style="max-width:220px" :placeholder="$t('admin.search_placeholder')" @keyup.enter="load">
        <select v-model="levelFilter" class="form-control form-control-sm" style="max-width:140px" @change="load">
          <option value="">{{ $t('admin.all_tiers') }}</option>
          <option v-for="opt in levelOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
        </select>
        <button class="btn btn-sm btn-primary" @click="load">{{ $t('common.filter') }}</button>
      </div>

      <div v-if="loading" class="text-muted">{{ $t('common.loading') }}</div>
      <div v-else class="table-responsive">
        <table class="table table-hover">
          <thead>
            <tr>
              <th>{{ $t('global.id') }}</th>
              <th>{{ $t('global.name') }}</th>
              <th>{{ $t('global.email') }}</th>
              <th>{{ $t('global.plan') }}</th>
              <th>{{ $t('global.level') }}</th>
              <th>{{ $t('global.status') }}</th>
              <th>{{ $t('admin.created') }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in users" :key="user.id">
              <td>{{ user.id }}</td>
              <td>{{ user.name }}</td>
              <td>{{ user.email }}</td>
              <td>
                <span class="badge plan-badge" :class="planBadgeClass(user.plan_slug)">
                  {{ user.plan_name || '-' }}
                </span>
              </td>
              <td><span class="badge" :class="user.level === 'admin' ? 'badge-primary' : 'badge-light'">{{ levelLabel(user.level) }}</span></td>
              <td><span class="badge" :class="user.status ? 'badge-success' : 'badge-danger'">{{ user.status ? $t('status.active') : $t('status.inactive') }}</span></td>
              <td>{{ formatDate(user.created_at) }}</td>
              <td>
                <button class="btn btn-xs btn-outline-primary" @click="openEdit(user)">{{ $t('common.edit') }}</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="text-muted small mb-0 mt-2">{{ $t('common.total_count', { count: usersTotal }) }}</p>
    </ContentCard>

    <modal name="edit-user-modal" height="auto" :adaptive="true" width="560" :scrollable="true">
      <div class="edit-user-modal" v-if="editForm">
        <div class="edit-user-modal__header">
          <div class="edit-user-modal__avatar">{{ userInitials }}</div>
          <div class="edit-user-modal__heading">
            <h5 class="mb-0">{{ $t('admin.edit_user') }} #{{ editForm.id }}</h5>
            <p class="text-muted small mb-0">{{ editForm.email }}</p>
          </div>
          <button type="button" class="edit-user-modal__close" :disabled="saving" @click="$modal.hide('edit-user-modal')">×</button>
        </div>

        <div v-if="editLoading" class="edit-user-modal__loading text-muted">{{ $t('common.loading') }}</div>

        <template v-else>
          <div class="edit-user-modal__section">
            <h6 class="edit-user-modal__section-title">{{ $t('admin.section_profile') }}</h6>
            <div class="row">
              <div class="col-md-6 form-group">
                <label>{{ $t('global.name') }}</label>
                <input v-model="editForm.name" class="form-control" />
              </div>
              <div class="col-md-6 form-group">
                <label>{{ $t('global.email') }}</label>
                <input v-model="editForm.email" type="email" class="form-control" />
              </div>
              <div class="col-12 form-group mb-0">
                <label>{{ $t('global.phone') }}</label>
                <input v-model="editForm.phone" class="form-control" />
              </div>
            </div>
          </div>

          <div class="edit-user-modal__section">
            <h6 class="edit-user-modal__section-title">{{ $t('admin.section_access') }}</h6>
            <div class="row">
              <div class="col-md-4 form-group">
                <label>{{ $t('global.level') }}</label>
                <select v-model="editForm.level" class="form-control">
                  <option v-for="opt in levelOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                </select>
              </div>
              <div class="col-md-4 form-group">
                <label>{{ $t('global.status') }}</label>
                <select v-model.number="editForm.status" class="form-control">
                  <option :value="1">{{ $t('status.active') }}</option>
                  <option :value="0">{{ $t('status.inactive') }}</option>
                </select>
              </div>
              <div class="col-md-4 form-group">
                <label>{{ $t('admin.user_plan') }}</label>
                <select v-model.number="editForm.plan_id" class="form-control">
                  <option v-for="plan in activePlans" :key="plan.id" :value="plan.id">
                    {{ plan.name }}
                  </option>
                </select>
                <small class="text-muted">{{ $t('admin.user_plan_hint') }}</small>
              </div>
            </div>
          </div>

          <div class="edit-user-modal__section">
            <h6 class="edit-user-modal__section-title">{{ $t('admin.section_security') }}</h6>
            <div class="form-group mb-0">
              <label>{{ $t('admin.new_password_optional') }}</label>
              <input v-model="editForm.password" type="password" class="form-control" autocomplete="new-password" />
            </div>
          </div>

          <div class="edit-user-modal__footer">
            <button class="btn btn-outline-secondary btn-sm" :disabled="saving" @click="onResetApiKey">
              <i class="fas fa-key mr-1"></i>{{ $t('admin.reset_api_key') }}
            </button>
            <div>
              <button class="btn btn-light btn-sm mr-2" :disabled="saving" @click="$modal.hide('edit-user-modal')">{{ $t('common.cancel') }}</button>
              <button class="btn btn-primary btn-sm" :disabled="saving" @click="saveUser">
                <span v-if="saving" class="spinner-border spinner-border-sm mr-1"></span>
                {{ $t('common.save') }}
              </button>
            </div>
          </div>
        </template>
      </div>
    </modal>
  </PageLayout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import i18nPage from '@/mixins/i18nPage'

export default {
  mixins: [i18nPage],
  data() {
    return {
      search: '',
      levelFilter: '',
      editForm: null,
      editLoading: false,
      saving: false,
    }
  },
  computed: {
    ...mapGetters('admin', ['users', 'usersTotal', 'loading', 'plans']),
    levelOptions() {
      return [
        { value: 'user', label: this.$t('admin.level_user') },
        { value: 'admin', label: this.$t('admin.level_admin') },
      ]
    },
    activePlans() {
      return (this.plans || []).filter((plan) => plan.is_active).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    },
    userInitials() {
      if (!this.editForm?.name) return '?'
      return this.editForm.name
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join('')
    },
  },
  async created() {
    await Promise.all([this.load(), this.fetchPlans()])
  },
  methods: {
    ...mapActions('admin', ['fetchUsers', 'fetchUser', 'fetchPlans', 'updateUser', 'resetUserApiKey']),
    async load() {
      await this.fetchUsers({ q: this.search, level: this.levelFilter, page: 1, limit: 50 })
    },
    formatDate(v) {
      if (!v) return '-'
      return new Date(v).toLocaleDateString(this.intlLocale)
    },
    levelLabel(level) {
      const opt = this.levelOptions.find((o) => o.value === level)
      return opt ? opt.label : level
    },
    planBadgeClass(slug) {
      if (slug === 'business') return 'plan-badge--business'
      if (slug === 'pro') return 'plan-badge--pro'
      return 'plan-badge--free'
    },
    async openEdit(user) {
      this.editForm = {
        id: user.id,
        name: user.name,
        email: user.email,
        phone: user.phone || '',
        level: user.level,
        status: user.status,
        plan_id: user.plan_id || null,
        password: '',
      }
      this.editLoading = true
      this.$modal.show('edit-user-modal')
      try {
        const detail = await this.fetchUser(user.id)
        if (detail) {
          this.editForm = {
            ...this.editForm,
            name: detail.name,
            email: detail.email,
            phone: detail.phone || '',
            level: detail.level,
            status: detail.status,
            plan_id: detail.plan_id || this.editForm.plan_id,
          }
        }
      } catch (e) {
        this.$toast?.error?.(e.response?.data?.message || this.$t('admin.load_user_failed'))
      } finally {
        this.editLoading = false
      }
    },
    async saveUser() {
      this.saving = true
      try {
        const payload = {
          name: this.editForm.name,
          email: this.editForm.email,
          phone: this.editForm.phone,
          level: this.editForm.level,
          status: this.editForm.status,
          plan_id: this.editForm.plan_id,
        }
        if (this.editForm.password) payload.password = this.editForm.password
        await this.updateUser({ id: this.editForm.id, payload })
        this.$toast?.success?.(this.$t('admin.user_updated'))
        this.$modal.hide('edit-user-modal')
        await this.load()
      } catch (e) {
        this.$toast?.error?.(e.response?.data?.message || this.$t('common.save_failed'))
      } finally {
        this.saving = false
      }
    },
    async onResetApiKey() {
      const ok = await this.$confirm(this.$t('admin.reset_api_confirm'), { danger: true })
      if (!ok) return
      try {
        const result = await this.resetUserApiKey(this.editForm.id)
        this.$toast?.success?.(this.$t('admin.api_key_reset', { key: result.api_key }))
      } catch (e) {
        this.$toast?.error?.(e.response?.data?.message || this.$t('common.save_failed'))
      }
    },
  },
}
</script>

<style scoped>
.gap-2 > * { margin-right: 0.5rem; margin-bottom: 0.5rem; }

.plan-badge {
  font-weight: 500;
  letter-spacing: 0.01em;
}
.plan-badge--free { background: #eef2f7; color: #5a6a7a; }
.plan-badge--pro { background: rgba(var(--wc-primary-rgb), 0.12); color: var(--wc-primary); }
.plan-badge--business { background: #fff3e0; color: #e65100; }

.edit-user-modal {
  padding: 0;
  max-height: 85vh;
  overflow-y: auto;
}

.edit-user-modal__header {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 1.25rem 1.35rem;
  border-bottom: 1px solid var(--wc-border, #e3e6ef);
  background: linear-gradient(135deg, rgba(var(--wc-primary-rgb), 0.06), transparent);
}

.edit-user-modal__avatar {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: var(--wc-primary);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.95rem;
  flex-shrink: 0;
}

.edit-user-modal__heading { flex: 1; min-width: 0; }

.edit-user-modal__close {
  border: none;
  background: transparent;
  font-size: 1.5rem;
  line-height: 1;
  color: var(--wc-text-muted, #6c757d);
  padding: 0 0.25rem;
}

.edit-user-modal__section {
  padding: 1.1rem 1.35rem;
  border-bottom: 1px solid var(--wc-border, #e3e6ef);
}

.edit-user-modal__section-title {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--wc-text-muted, #6c757d);
  margin-bottom: 0.85rem;
}

.edit-user-modal__loading {
  padding: 2rem 1.35rem;
  text-align: center;
}

.edit-user-modal__footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 1rem 1.35rem 1.25rem;
  background: var(--wc-bg-subtle, #f8f9fa);
}
</style>
